import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Box, Button, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Chip, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  CircularProgress, Autocomplete
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import { getFirestore, collection, getDocs, addDoc, deleteDoc, doc, updateDoc, query, where } from 'firebase/firestore';
import RefreshIcon from '@mui/icons-material/Refresh';

const MandatoryItemsScreen = () => {
  const [mandatoryItems, setMandatoryItems] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [newItem, setNewItem] = useState({ inventoryItem: null, quantity: 1 });
  const [editItem, setEditItem] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');

  const db = getFirestore();

  useEffect(() => {
    fetchMandatoryItems();
    fetchInventoryItems();
  }, []);

  const fetchMandatoryItems = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'mandatoryItems'), where('isActive', '==', true));
      const snapshot = await getDocs(q);
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMandatoryItems(items);
    } catch (err) {
      setError('Failed to fetch mandatory items');
    } finally {
      setLoading(false);
    }
  };

  const fetchInventoryItems = async () => {
    try {
      setLoadingInventory(true);
      const snapshot = await getDocs(collection(db, 'inventoryItems'));
      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      // Filter out items with 0 available quantity
      const activeItems = items.filter(item => item.availableQuantity > 0);
      setInventoryItems(activeItems);
    } catch (err) {
      setError('Failed to fetch inventory items');
    } finally {
      setLoadingInventory(false);
    }
  };

  const handleAddItem = async () => {
    if (!newItem.inventoryItem) return;
    
    try {
      await addDoc(collection(db, 'mandatoryItems'), {
        inventoryItemId: newItem.inventoryItem.id,
        title: newItem.inventoryItem.title,
        refId: newItem.inventoryItem.id,
        collection: 'inventoryItems',
        quantity: parseInt(newItem.quantity),
        units: newItem.inventoryItem.units || 'pcs',
        isActive: true,
        createdAt: new Date().toISOString()
      });
      setNewItem({ inventoryItem: null, quantity: 1 });
      setSearchText('');
      fetchMandatoryItems();
    } catch (err) {
      setError('Failed to add item');
    }
  };

  const handleDeleteItem = async (id) => {
    try {
      await deleteDoc(doc(db, 'mandatoryItems', id));
      fetchMandatoryItems();
    } catch (err) {
      setError('Failed to delete item');
    }
  };

  const handleEditItem = (item) => {
    setEditItem(item);
    setOpenDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!editItem.title.trim()) return;
    
    try {
      await updateDoc(doc(db, 'mandatoryItems', editItem.id), {
        title: editItem.title.trim(),
        quantity: parseInt(editItem.quantity),
        updatedAt: new Date().toISOString()
      });
      setOpenDialog(false);
      setEditItem(null);
      fetchMandatoryItems();
    } catch (err) {
      setError('Failed to update item');
    }
  };

  const filteredInventoryItems = inventoryItems.filter(item =>
    item.title.toLowerCase().includes(searchText.toLowerCase())
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Mandatory Items Management
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Add New Item with Searchable Dropdown */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'end' }}>
          <Autocomplete
            options={filteredInventoryItems}
            getOptionLabel={(option) => option.title}
            value={newItem.inventoryItem}
            onChange={(event, newValue) => {
              setNewItem({ ...newItem, inventoryItem: newValue });
              setSearchText(newValue?.title || '');
            }}
            inputValue={searchText}
            onInputChange={(event, newInputValue) => setSearchText(newInputValue)}
            filterOptions={(x) => x} // Use filteredInventoryItems
            noOptionsText={
              loadingInventory ? "Loading inventory..." : 
              searchText ? "No items match your search" : 
              "No items available"
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search Inventory Items"
                placeholder="Type to search items..."
                size="small"
                sx={{ minWidth: 350 }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
                  endAdornment: (
                    <>
                      {loadingInventory ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props} sx={{ py: 1 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body2" fontWeight="medium">
                    {option.title}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {option.brand || option.vendor || 'No brand'} • {option.availableQuantity} {option.units} available
                  </Typography>
                </Box>
              </Box>
            )}
            loading={loadingInventory}
          />
          
          <TextField
            label="Default Quantity"
            type="number"
            value={newItem.quantity}
            onChange={(e) => setNewItem({ ...newItem, quantity: e.target.value })}
            size="small"
            sx={{ width: 120 }}
            inputProps={{ min: 1 }}
            disabled={!newItem.inventoryItem}
          />
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddItem}
            disabled={!newItem.inventoryItem || !newItem.quantity}
          >
            Add Item
          </Button>
          
          <Button
            variant="outlined"
            onClick={fetchInventoryItems}
            startIcon={<RefreshIcon />}
            disabled={loadingInventory}
          >
            Refresh Inventory
          </Button>
        </Box>

        {/* Items Table */}
        <TableContainer sx={{ minHeight: 300 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Item Name</TableCell>
                <TableCell>Inventory ID</TableCell>
                <TableCell>Default Quantity</TableCell>
                <TableCell>Units</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {mandatoryItems.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {item.title}
                      </Typography>
                      {item.inventoryItemId && (
                        <Typography variant="caption" color="text.secondary">
                          From inventory
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontSize="0.875rem">
                      {item.inventoryItemId || 'N/A'}
                    </Typography>
                  </TableCell>
                  <TableCell>{item.quantity}</TableCell>
                  <TableCell>{item.units || 'pcs'}</TableCell>
                  <TableCell>
                    <Chip label="Active" color="success" size="small" />
                  </TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleEditItem(item)} size="small">
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      onClick={() => handleDeleteItem(item.id)} 
                      color="error"
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {mandatoryItems.length === 0 && !loading && (
          <Alert severity="info" sx={{ mt: 2 }}>
            No mandatory items configured. Search and add items from your inventory that should be included in every order.
          </Alert>
        )}

        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            💡 Only items with available stock ({'>'}0) from inventory will appear in the dropdown.
          </Typography>
        </Box>
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Mandatory Item</DialogTitle>
        <DialogContent>
          <TextField
            label="Item Name"
            value={editItem?.title || ''}
            onChange={(e) => setEditItem({ ...editItem, title: e.target.value })}
            fullWidth
            margin="dense"
          />
          <TextField
            label="Quantity"
            type="number"
            value={editItem?.quantity || 1}
            onChange={(e) => setEditItem({ ...editItem, quantity: e.target.value })}
            fullWidth
            margin="dense"
            inputProps={{ min: 1 }}
          />
          <TextField
            label="Units"
            value={editItem?.units || 'pcs'}
            onChange={(e) => setEditItem({ ...editItem, units: e.target.value })}
            fullWidth
            margin="dense"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MandatoryItemsScreen;
